import { createContext, useContext, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import io from "socket.io-client";
import { signOutRequest, updateUser } from "store/auth/authSlice";
import { socketUrl } from "../config/apiUrl";
import { setUnReadNotificationCount } from "../store/common/commonSlice";

const WebSocketContext = createContext(null);

export const WebSocketProvider = ({ children }) => {
  const { isLogin, user, access_token } = useSelector(
    (state) => state.authReducer,
  );
  const socketRef = useRef(null);
  // Latest user, read by socket handlers. Keeping it in a ref means the socket
  // does NOT have to be torn down and re-created every time the user object
  // changes — that churn caused dropped connections, duplicated online/offline
  // updates, and stale event handlers in pages that subscribe to socket.current.
  const userRef = useRef(user);

  const dispatch = useDispatch();

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    if (!isLogin || !access_token) {
      return undefined;
    }

    const socket = io(socketUrl, {
      query: { app: "fluency-compass" },
      auth: { token: access_token },
      transports: ["websocket"],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      randomizationFactor: 0,
    });
    socketRef.current = socket;

    socket.on("new-notification", (unreadCount) => {
      dispatch(setUnReadNotificationCount(unreadCount));
    });
    socket.on("user-updated", (data) => {
      // dispatch(updateUser(data?.user));
      dispatch(updateUser({ ...userRef.current, ...data?.user }));
    });
    socket.on("school-updated", (data) => {
      dispatch(updateUser({ ...userRef.current, school: data?.school }));
    });
    socket.on("user-deleted", (data) => {
      if (data?.user?._id == userRef.current?._id) {
        dispatch(signOutRequest());
        window.location.href = "/login";
      }
    });

    // Surface connection failures so socket issues are diagnosable in prod.
    socket.on("connect_error", (error) => {
      console.error("[socket] connect_error:", error?.message);
    });

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
      if (socketRef.current === socket) {
        socketRef.current = null;
      }
    };
  }, [isLogin, access_token, dispatch]);

  return (
    <WebSocketContext.Provider value={socketRef}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => useContext(WebSocketContext);
